import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Brain, Sparkles, Bot, Volume2, Phone, Mail, MessageCircle, Hash,
  Facebook, Linkedin, Twitter, Target, Video, CreditCard, Wallet,
  Calendar, CalendarCheck, HardDrive, Database, Cloud, Zap, BarChart,
  PieChart, CheckCircle, XCircle, ExternalLink, RefreshCw, Save, Trash2,
  Search, Filter, Settings, Share2, IndianRupee, Users, TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  brain: Brain,
  sparkles: Sparkles,
  bot: Bot,
  volume2: Volume2,
  phone: Phone,
  mail: Mail,
  'message-circle': MessageCircle,
  'message-square': MessageCircle,
  hash: Hash,
  facebook: Facebook,
  linkedin: Linkedin,
  twitter: Twitter,
  target: Target,
  video: Video,
  'credit-card': CreditCard,
  wallet: Wallet,
  'indian-rupee': IndianRupee,
  calendar: Calendar,
  'calendar-check': CalendarCheck,
  'hard-drive': HardDrive,
  database: Database,
  cloud: Cloud,
  zap: Zap,
  'bar-chart': BarChart,
  'pie-chart': PieChart,
  'share-2': Share2,
  users: Users,
  'trending-up': TrendingUp
};

// Color mapping
const colorMap = {
  green: 'bg-green-100 text-green-600',
  orange: 'bg-orange-100 text-orange-600',
  blue: 'bg-blue-100 text-blue-600',
  violet: 'bg-violet-100 text-violet-600',
  red: 'bg-red-100 text-red-600',
  purple: 'bg-purple-100 text-purple-600',
  sky: 'bg-sky-100 text-sky-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  pink: 'bg-pink-100 text-pink-600',
  indigo: 'bg-indigo-100 text-indigo-600'
};

// Maps a marketing lead-source (the channel that appears in the Marketing
// Dashboard's Source Analytics — meta / google / tiktok / etc.) to the
// integration IDs / name substrings that actually feed it. Matching is
// substring-based so backend id changes (e.g. `meta` → `meta_ads`) don't
// silently drop the mapping. Unknown ids just filter out — no error.
const SOURCE_INTEGRATION_MAP = {
  meta:       ['meta_ads', 'meta', 'facebook'],
  google:     ['google_ads', 'google_analytics', 'google'],
  tiktok:     ['tiktok_ads', 'tiktok'],
  linkedin:   ['linkedin_ads', 'linkedin'],
  youtube:    ['youtube'],
  whatsapp:   ['whatsapp', 'green-api', 'green_api'],
  sms_voice:  ['twilio', 'elevenlabs'],
  email:      ['sendgrid', 'resend', 'microsoft365', 'microsoft_365', 'gmail', 'outlook'],
  crm:        ['hubspot', 'salesforce', 'zoho'],
  website:    ['website_webhook', 'website', 'webhook', 'form'],
};

const SOURCE_OPTIONS = [
  { value: 'all',       label: 'All sources' },
  { value: 'meta',      label: 'Meta Ads' },
  { value: 'google',    label: 'Google Ads / Analytics' },
  { value: 'tiktok',    label: 'TikTok Ads' },
  { value: 'linkedin',  label: 'LinkedIn Ads' },
  { value: 'youtube',   label: 'YouTube' },
  { value: 'whatsapp',  label: 'WhatsApp' },
  { value: 'sms_voice', label: 'SMS / Voice' },
  { value: 'email',     label: 'Email' },
  { value: 'crm',       label: 'CRM Sync' },
  { value: 'website',   label: 'Website / Forms' },
];

// ---------------------------------------------------------------------------
// Marketing Sources settings panel — three collapsible sections rendered
// when a specific source (Meta / Google / etc.) is picked in the dropdown
// inside the Email & Marketing tab.
//
//   1. Connectivity Settings — one card per integration that feeds this
//      source (credentials, connection status, "Configure" button that
//      opens the shared credential dialog).
//   2. Marketing Dashboard Configuration — toggles that control how this
//      source is reflected on the Marketing Dashboard (show / hide,
//      include in totals, colour rail).
//   3. Marketing User Source Link — free-text list of user emails / role
//      codes that should own leads coming in via this source.
//
// All settings are stored locally in component state and persisted through
// the existing PUT /api/organization/settings/marketing_sources endpoint
// (a lightweight key-value blob keyed by source id). If that endpoint
// isn't available the settings simply live in memory for the session —
// they don't block anything else on the page.
// ---------------------------------------------------------------------------
const MarketingSourceSettings = ({
  sourceValue, sourceLabel, integrations, onOpenConfig, getIcon, getColorClass, token,
}) => {
  const [dashboardCfg, setDashboardCfg] = useState({
    show_on_dashboard: true,
    include_in_kpis:   true,
    include_in_ai_insights: true,
  });
  const [userLink, setUserLink] = useState('');
  const [saving, setSaving] = useState(false);

  // Try to hydrate saved settings for this source. Silent-fail on 404
  // (endpoint may not exist yet) so the panel still renders.
  useEffect(() => {
    if (!token || !sourceValue) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/organization/settings/marketing_sources?token=${token}&source=${sourceValue}`,
        );
        if (cancelled) return;
        const s = r.data?.data || r.data || {};
        if (s.dashboard_cfg) setDashboardCfg((cur) => ({ ...cur, ...s.dashboard_cfg }));
        if (typeof s.user_link === 'string') setUserLink(s.user_link);
      } catch (_) { /* stay with defaults */ }
    })();
    return () => { cancelled = true; };
  }, [token, sourceValue]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/organization/settings/marketing_sources?token=${token}`,
        { source: sourceValue, dashboard_cfg: dashboardCfg, user_link: userLink },
      );
      toast.success(`Saved ${sourceLabel} settings`);
    } catch (e) {
      // 404 = endpoint not implemented yet. Warn but don't error-toast.
      if (e?.response?.status === 404) {
        toast.message('Settings kept in this session (backend save endpoint not wired yet)');
      } else {
        toast.error(e?.response?.data?.detail || 'Could not save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Section 1 — Connectivity */}
      <section>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <Zap size={14} className="text-emerald-500" />
          Connectivity Settings — {sourceLabel}
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          API credentials and connection status for every integration that feeds this source.
        </p>
        {integrations.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No integrations wired to this source yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {integrations.map((integration) => {
              const Icon = getIcon(integration.icon);
              const configured = !!integration.configured || integration.status === 'active';
              return (
                <div key={integration.id}
                     className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
                  <div className={`p-2 rounded-md ${getColorClass(integration.color)}`}>
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{integration.name}</p>
                    <p className="text-xs text-slate-500 truncate">{integration.description}</p>
                  </div>
                  <Badge variant={configured ? 'default' : 'outline'} className="shrink-0 text-[10px]">
                    {configured ? 'Connected' : 'Not configured'}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => onOpenConfig(integration)}>
                    Configure
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Section 2 — Marketing Dashboard Configuration */}
      <section>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <BarChart size={14} className="text-blue-500" />
          Marketing Dashboard Configuration
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Control how leads from {sourceLabel} appear on the Marketing Dashboard.
        </p>
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          {[
            { key: 'show_on_dashboard',      label: 'Show on Marketing Dashboard', hint: 'Reveal this source in the Source Analytics section.' },
            { key: 'include_in_kpis',        label: 'Include in KPI totals',        hint: 'Roll leads from this source into the Performance KPIs.' },
            { key: 'include_in_ai_insights', label: 'Include in AI Insights',       hint: 'Let AI Insights consider this source when ranking Top Source cards.' },
          ].map((row) => (
            <label key={row.key} className="flex items-center justify-between gap-3 cursor-pointer">
              <div className="min-w-0">
                <p className="text-sm text-slate-800">{row.label}</p>
                <p className="text-xs text-slate-500">{row.hint}</p>
              </div>
              <Switch
                checked={!!dashboardCfg[row.key]}
                onCheckedChange={(v) => setDashboardCfg((cur) => ({ ...cur, [row.key]: !!v }))}
              />
            </label>
          ))}
        </div>
      </section>

      {/* Section 3 — Marketing User Source Link */}
      <section>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <Users size={14} className="text-violet-500" />
          Marketing User Source Link
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Users (emails or role codes) who should be assigned leads that come in via {sourceLabel}. One per line, or comma-separated.
        </p>
        <Textarea
          value={userLink}
          onChange={(e) => setUserLink(e.target.value)}
          placeholder={"e.g.\nagent1@company.com\nmarketing_agent\nlead.owner@company.com"}
          rows={4}
        />
      </section>

      <div className="flex justify-end gap-2">
        <Button onClick={handleSave} disabled={saving} data-testid="save-marketing-source">
          <Save size={14} className="mr-1.5" />
          {saving ? 'Saving...' : 'Save settings'}
        </Button>
      </div>
    </div>
  );
};

const Integrations = () => {
  const { token } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  
  // Dialog state
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [loadingWhatsappStatus, setLoadingWhatsappStatus] = useState(false);

  const fetchIntegrations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations/all?token=${token}`);
      if (response.data.success) {
        setIntegrations(response.data.integrations);
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchIntegrations();
    }
  }, [token, fetchIntegrations]);

  const handleOpenConfig = (integration) => {
    setSelectedIntegration(integration);
    setCredentials({});
    setWhatsappStatus(null);
    setIsDialogOpen(true);
    
    // Fetch WhatsApp status if it's the WhatsApp integration
    if (integration.id === 'whatsapp') {
      fetchWhatsappStatus();
    }
  };

  const fetchWhatsappStatus = async () => {
    setLoadingWhatsappStatus(true);
    try {
      const response = await axios.get(`${API}/integrations/whatsapp/status?token=${token}`);
      setWhatsappStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch WhatsApp status:', error);
      setWhatsappStatus({ success: false, status: 'error', message: 'Failed to check status' });
    } finally {
      setLoadingWhatsappStatus(false);
    }
  };

  const handleSendTestMessage = async () => {
    const phone = prompt('Enter phone number to send test message (e.g., +971501234567):');
    if (!phone) return;
    
    setTesting(true);
    try {
      const response = await axios.post(
        `${API}/integrations/whatsapp/test-message?token=${token}&phone=${encodeURIComponent(phone)}&message=${encodeURIComponent('Test message from CRM - Integration is working!')}`
      );
      if (response.data.success) {
        toast.success('Test message sent successfully!');
      } else {
        toast.error(response.data.error || 'Failed to send test message');
      }
    } catch (error) {
      toast.error('Failed to send test message');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!selectedIntegration) return;
    
    // Validate required fields
    const missingFields = selectedIntegration.fields.filter(f => !credentials[f]);
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/integrations/${selectedIntegration.id}/save?token=${token}`,
        credentials
      );
      
      if (response.data.success) {
        toast.success(response.data.message || 'Integration saved!');
        setIsDialogOpen(false);
        fetchIntegrations();
      } else {
        toast.error(response.data.error || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedIntegration) return;
    
    setTesting(true);
    try {
      // First save if there are new credentials
      if (Object.keys(credentials).length > 0) {
        await axios.post(
          `${API}/integrations/${selectedIntegration.id}/save?token=${token}`,
          credentials
        );
      }
      
      const response = await axios.post(
        `${API}/integrations/${selectedIntegration.id}/test?token=${token}`
      );
      
      if (response.data.success) {
        toast.success(response.data.message || 'Connection successful!');
        fetchIntegrations();
      } else {
        toast.error(response.data.error || 'Connection failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteIntegration = async () => {
    if (!selectedIntegration) return;
    
    if (!confirm(`Are you sure you want to remove ${selectedIntegration.name}?`)) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/integrations/${selectedIntegration.id}?token=${token}`
      );
      
      if (response.data.success) {
        toast.success('Integration removed');
        setIsDialogOpen(false);
        fetchIntegrations();
      } else {
        toast.error(response.data.error || 'Failed to remove');
      }
    } catch (error) {
      toast.error('Failed to remove integration');
    }
  };

  const handleSyncHubSpotContacts = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/integrations/hubspot/sync?token=${token}`);
      
      if (response.data.success) {
        const { imported, updated, errors, total } = response.data;
        toast.success(`Sync complete: ${imported} imported, ${updated} updated from ${total} contacts`);
        if (errors > 0) {
          toast.warning(`${errors} contacts had errors during sync`);
        }
      } else {
        toast.error(response.data.message || 'Sync failed');
      }
    } catch (error) {
      console.error('HubSpot sync error:', error);
      toast.error('Failed to sync contacts from HubSpot');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncHubSpotOwners = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/integrations/hubspot/sync-owners?token=${token}`);
      
      if (response.data.success) {
        const { created, updated, synced } = response.data;
        toast.success(`Synced ${synced} sales agents: ${created} created, ${updated} updated`);
      } else {
        toast.error(response.data.message || 'Sync failed');
      }
    } catch (error) {
      console.error('HubSpot owner sync error:', error);
      toast.error('Failed to sync sales agents from HubSpot');
    } finally {
      setSyncing(false);
    }
  };

  // Filter integrations
  const filteredIntegrations = integrations.filter(integration => {
    const matchesCategory = activeCategory === 'all' || integration.category === activeCategory;
    const matchesSearch = searchQuery === '' ||
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // For the Marketing Sources panel inside Email & Marketing: given a
  // selected source, resolve the integrations that feed it (substring
  // match on id + name, same map as before). 'all' returns an empty list
  // so the panel falls back to the regular email cards below.
  const marketingSourceIntegrations = sourceFilter === 'all' ? [] :
    integrations.filter((integration) => {
      const needles = SOURCE_INTEGRATION_MAP[sourceFilter] || [];
      const hay = `${integration.id || ''} ${integration.name || ''}`.toLowerCase();
      return needles.some((n) => hay.includes(n));
    });

  // Group by category
  const groupedIntegrations = categories.map(category => ({
    ...category,
    integrations: filteredIntegrations.filter(i => i.category === category.id)
  })).filter(group => group.integrations.length > 0);

  const getIcon = (iconName) => {
    return iconMap[iconName] || Settings;
  };

  const getColorClass = (color) => {
    return colorMap[color] || 'bg-slate-100 text-slate-600';
  };

  const getFieldLabel = (field) => {
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const isFieldSensitive = (field) => {
    const sensitiveFields = ['api_key', 'secret', 'token', 'password', 'auth_token', 'client_secret'];
    return sensitiveFields.some(s => field.toLowerCase().includes(s));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="integrations-loading">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  const connectedCount = integrations.filter(i => i.configured).length;

  return (
    <div data-testid="integrations-page" className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-primary">Integrations</h1>
        <p className="text-slate-500 mt-1">
          Connect your favorite tools and services • {connectedCount} of {integrations.length} connected
        </p>
      </div>

      {/* Search on its own row so it can span the full width. */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search integrations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="integration-search"
          />
        </div>
      </div>

      {/* Filter row — category tabs. The marketing-source dropdown was
          removed from here and is now scoped to the Email & Marketing
          tab (see the Marketing Sources panel below). */}
      <div className="mb-6 flex items-center gap-2 overflow-x-auto pb-2">
        <Button
          variant={activeCategory === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveCategory('all')}
          data-testid="filter-all"
        >
          All ({integrations.length})
        </Button>
        {categories.map((cat) => {
          const count = integrations.filter(i => i.category === cat.id).length;
          const CatIcon = getIcon(cat.icon);
          return (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`filter-${cat.id}`}
            >
              <CatIcon size={14} className="mr-1" />
              {cat.name} ({count})
            </Button>
          );
        })}
      </div>

      {/* Marketing Sources panel — Email & Marketing tab only. Heading
          on the left, source picker on the right. When a specific source
          is picked, the settings panel below replaces the regular email
          cards; 'All sources' keeps the cards visible. */}
      {activeCategory === 'email' && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
                <Target size={18} className="text-violet-500" />
                Marketing Sources
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Pick a lead-source channel to configure its connectivity, dashboard visibility, and user link.
              </p>
            </div>
            <div className="shrink-0">
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="h-9 w-[220px] text-sm" data-testid="marketing-source-select">
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {sourceFilter !== 'all' && (
            <MarketingSourceSettings
              sourceValue={sourceFilter}
              sourceLabel={SOURCE_OPTIONS.find((o) => o.value === sourceFilter)?.label || sourceFilter}
              integrations={marketingSourceIntegrations}
              onOpenConfig={handleOpenConfig}
              getIcon={getIcon}
              getColorClass={getColorClass}
              token={token}
            />
          )}
        </div>
      )}

      {/* Integration Grid — regular category-scoped card grid. Hidden
          when the Marketing Sources panel above is showing a specific
          source's settings, since those cards would duplicate the
          panel's Connectivity list. */}
      <div className="space-y-8" hidden={activeCategory === 'email' && sourceFilter !== 'all'}>
        {activeCategory === 'all' ? (
          // Show grouped by category
          groupedIntegrations.map((group) => (
            <div key={group.id}>
              <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                {(() => {
                  const GroupIcon = getIcon(group.icon);
                  return <GroupIcon size={20} />;
                })()}
                {group.name}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.integrations.map((integration) => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onConfigure={handleOpenConfig}
                    getIcon={getIcon}
                    getColorClass={getColorClass}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          // Show flat list for selected category
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredIntegrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onConfigure={handleOpenConfig}
                getIcon={getIcon}
                getColorClass={getColorClass}
              />
            ))}
          </div>
        )}

        {filteredIntegrations.length === 0 && (
          <div className="text-center py-12">
            <Search size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No integrations found</p>
          </div>
        )}
      </div>

      {/* Configuration Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="integration-dialog">
          {selectedIntegration && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${getColorClass(selectedIntegration.color)}`}>
                    {(() => {
                      const Icon = getIcon(selectedIntegration.icon);
                      return <Icon size={24} />;
                    })()}
                  </div>
                  <div>
                    <DialogTitle>{selectedIntegration.name}</DialogTitle>
                    <DialogDescription>{selectedIntegration.description}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Status */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-600">Status</span>
                  {selectedIntegration.configured ? (
                    <Badge className="bg-green-500 text-white">
                      <CheckCircle size={12} className="mr-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge variant="outline">Not Configured</Badge>
                  )}
                </div>

                {/* WhatsApp Live Status */}
                {selectedIntegration.id === 'whatsapp' && (
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-green-800 flex items-center gap-2">
                        <MessageCircle size={16} />
                        WhatsApp Connection Status
                      </h4>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={fetchWhatsappStatus}
                        disabled={loadingWhatsappStatus}
                      >
                        <RefreshCw size={14} className={loadingWhatsappStatus ? 'animate-spin' : ''} />
                      </Button>
                    </div>
                    
                    {loadingWhatsappStatus ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <RefreshCw size={14} className="animate-spin" />
                        Checking connection...
                      </div>
                    ) : whatsappStatus ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {whatsappStatus.is_authorized ? (
                            <CheckCircle size={18} className="text-green-600" />
                          ) : (
                            <XCircle size={18} className="text-amber-500" />
                          )}
                          <span className={`font-medium ${whatsappStatus.is_authorized ? 'text-green-700' : 'text-amber-700'}`}>
                            {whatsappStatus.is_authorized ? 'Connected & Authorized' : `Status: ${whatsappStatus.status}`}
                          </span>
                        </div>
                        {whatsappStatus.instance_id && (
                          <p className="text-xs text-slate-500">
                            Instance: {whatsappStatus.instance_id}
                          </p>
                        )}
                        {whatsappStatus.is_authorized && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={handleSendTestMessage}
                            disabled={testing}
                            className="mt-2"
                          >
                            {testing ? (
                              <RefreshCw size={14} className="mr-2 animate-spin" />
                            ) : (
                              <MessageCircle size={14} className="mr-2" />
                            )}
                            Send Test Message
                          </Button>
                        )}
                        {!whatsappStatus.is_authorized && whatsappStatus.status !== 'not_configured' && (
                          <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                            <p className="text-xs text-amber-700">
                              WhatsApp needs re-authorization. Please scan the QR code in your Green-API dashboard.
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Click refresh to check status</p>
                    )}
                  </div>
                )}

                {/* Credential Fields */}
                <div className="space-y-3">
                  {selectedIntegration.fields.map((field) => (
                    <div key={field}>
                      <Label htmlFor={field}>{getFieldLabel(field)}</Label>
                      <Input
                        id={field}
                        type={isFieldSensitive(field) ? 'password' : 'text'}
                        value={credentials[field] || ''}
                        onChange={(e) => setCredentials({ ...credentials, [field]: e.target.value })}
                        placeholder={
                          selectedIntegration.configured 
                            ? selectedIntegration.credentials?.[field] || `Enter ${getFieldLabel(field)}`
                            : `Enter ${getFieldLabel(field)}`
                        }
                        className="mt-1"
                        data-testid={`input-${field}`}
                      />
                    </div>
                  ))}
                </div>

                {/* Documentation Link */}
                {selectedIntegration.docs_url && (
                  <a
                    href={selectedIntegration.docs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-accent hover:underline"
                  >
                    <ExternalLink size={14} />
                    Get credentials from {selectedIntegration.name}
                  </a>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleTestConnection}
                    variant="outline"
                    disabled={testing || saving}
                    className="flex-1"
                    data-testid="test-connection-btn"
                  >
                    {testing ? (
                      <RefreshCw size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Zap size={14} className="mr-2" />
                    )}
                    Test
                  </Button>
                  <Button
                    onClick={handleSaveCredentials}
                    disabled={saving || testing}
                    className="flex-1"
                    data-testid="save-integration-btn"
                  >
                    {saving ? (
                      <RefreshCw size={14} className="mr-2 animate-spin" />
                    ) : (
                      <Save size={14} className="mr-2" />
                    )}
                    Save
                  </Button>
                </div>

                {/* Delete Button */}
                {selectedIntegration.configured && (
                  <Button
                    onClick={handleDeleteIntegration}
                    variant="ghost"
                    className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="delete-integration-btn"
                  >
                    <Trash2 size={14} className="mr-2" />
                    Remove Integration
                  </Button>
                )}

                {/* HubSpot Sync Button */}
                {selectedIntegration.id === 'hubspot' && selectedIntegration.configured && (
                  <div className="pt-2 border-t space-y-4">
                    <Button
                      onClick={handleSyncHubSpotContacts}
                      variant="outline"
                      disabled={syncing}
                      className="w-full bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700"
                      data-testid="sync-hubspot-btn"
                    >
                      {syncing ? (
                        <RefreshCw size={14} className="mr-2 animate-spin" />
                      ) : (
                        <RefreshCw size={14} className="mr-2" />
                      )}
                      Sync All Contacts from HubSpot
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                      Import contacts with source, lifecycle stage, and lead status
                    </p>
                    
                    {/* Sync Sales Agents Button */}
                    <Button
                      onClick={handleSyncHubSpotOwners}
                      variant="outline"
                      disabled={syncing}
                      className="w-full bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700"
                      data-testid="sync-hubspot-owners-btn"
                    >
                      {syncing ? (
                        <RefreshCw size={14} className="mr-2 animate-spin" />
                      ) : (
                        <Users size={14} className="mr-2" />
                      )}
                      Sync Sales Agents from HubSpot
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                      Import HubSpot owners as sales agents with automatic lead assignment
                    </p>
                    
                    {/* Webhook Setup Instructions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                        <Zap size={16} />
                        Real-Time Sync (Webhook)
                      </h4>
                      <p className="text-xs text-blue-700 mb-3">
                        Set up a webhook to automatically sync new contacts when they're created in HubSpot:
                      </p>
                      <div className="bg-white rounded p-2 mb-3">
                        <p className="text-xs text-slate-500 mb-1">Webhook URL:</p>
                        <code className="text-xs text-blue-800 font-mono break-all select-all">
                          https://sales-copilot-ai.preview.emergentagent.com/api/webhooks/hubspot
                        </code>
                      </div>
                      <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                        <li>Go to HubSpot → Settings → Integrations → Private Apps</li>
                        <li>Select your Private App → "Webhooks" tab</li>
                        <li>Click "Create subscription"</li>
                        <li>Paste the webhook URL above</li>
                        <li>Select CRM → Contacts → "Create" event</li>
                        <li>Click "Subscribe" to activate</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Integration Card Component
const IntegrationCard = ({ integration, onConfigure, getIcon, getColorClass }) => {
  const Icon = getIcon(integration.icon);
  
  return (
    <Card 
      className={`p-4 cursor-pointer transition-all hover:shadow-md ${
        integration.configured ? 'border-green-200 bg-green-50/30' : ''
      }`}
      onClick={() => onConfigure(integration)}
      data-testid={`integration-card-${integration.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${getColorClass(integration.color)}`}>
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-primary truncate">{integration.name}</h3>
            {integration.configured ? (
              <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
            ) : null}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{integration.description}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Badge variant="outline" className="text-xs capitalize">
          {integration.category.replace('_', ' ')}
        </Badge>
        {integration.configured ? (
          <span className="text-xs text-green-600">Connected</span>
        ) : (
          <span className="text-xs text-slate-400">Click to configure</span>
        )}
      </div>
    </Card>
  );
};

export default Integrations;
